import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Phone, MapPin, Clock, Search } from 'lucide-react';

/**
 * PWA Home Screen - Premium Industrial Design
 * Inspiriert von Hilti, Würth, Amazon Business
 */
const PWAHomeScreen = () => {
  // Kategorien mit echten Produktbildern
  const categories = [
    { 
      id: 'bagger', 
      name: 'Bagger', 
      subtitle: '8 Modelle',
      path: '/bagger', 
      image: 'https://customer-assets.emergentagent.com/job_industrial-shop-3/artifacts/6712clnt_71B63BD5-F262-475C-9EA5-4A28924A64D1.png'
    },
    // { id: 'radlader', name: 'Radlader', subtitle: '2 Modelle', path: '/radlader', image: 'https://customer-assets.emergentagent.com/job_99a08884-63b0-48b0-bdd8-26682827bf4f/artifacts/at8xy4yy_roter%20radlader.png' }, // Ausgeblendet
    { 
      id: 'gabelstapler', 
      name: 'Gabelstapler', 
      subtitle: '2 Modelle',
      path: '/gabelstapler', 
      image: 'https://customer-assets.emergentagent.com/job_99a08884-63b0-48b0-bdd8-26682827bf4f/artifacts/f1sdirt5_Gabelstapler%20rot.png'
    },
    { 
      id: 'ruettelplatten', 
      name: 'Rüttelplatten', 
      subtitle: '2 Modelle',
      path: '/ruettelplatten', 
      image: 'https://customer-assets.emergentagent.com/job_99a08884-63b0-48b0-bdd8-26682827bf4f/artifacts/480nx5kd_R%C3%BCttelplatte%20rot-1.png'
    },
    { 
      id: 'stampfer', 
      name: 'Stampfer', 
      subtitle: '2 Modelle',
      path: '/stampfer', 
      image: 'https://customer-assets.emergentagent.com/job_99a08884-63b0-48b0-bdd8-26682827bf4f/artifacts/jioag6dr_Stampfer%20rot.png'
    },
    { 
      id: 'zubehoer', 
      name: 'Zubehör', 
      subtitle: '12+ Artikel',
      path: '/zubehoer', 
      image: 'https://static.prod-images.emergentagent.com/jobs/080d9d8d-9c8d-40f1-9d9b-f2b78b704564/images/e6bcf0e8f5de7795286d2855b5ff14ec9332ab70e36f1de152d4275de1f04fa2.png'
    },
  ];

  // Featured Products mit echten Daten
  const featuredProducts = [
    { 
      id: 1, 
      name: 'QW10', 
      subtitle: 'Mini-Bagger 1t', 
      price: '8.990', 
      image: 'https://customer-assets.emergentagent.com/job_industrial-shop-3/artifacts/6712clnt_71B63BD5-F262-475C-9EA5-4A28924A64D1.png', 
      path: '/bagger/mini-bagger-qv10',
      badge: 'Bestseller'
    },
    { 
      id: 2, 
      name: 'QW12', 
      subtitle: 'Mini-Bagger 1,2t', 
      price: '9.990', 
      image: 'https://customer-assets.emergentagent.com/job_industrial-shop-3/artifacts/6712clnt_71B63BD5-F262-475C-9EA5-4A28924A64D1.png', 
      path: '/bagger/mini-bagger-qv12',
      badge: 'Sofort lieferbar'
    },
    // Radlader-Eintrag ausgeblendet
    { 
      id: 4, 
      name: 'QV15', 
      subtitle: 'Mini-Bagger 1,5t', 
      price: '12.990', 
      image: 'https://customer-assets.emergentagent.com/job_industrial-shop-3/artifacts/6712clnt_71B63BD5-F262-475C-9EA5-4A28924A64D1.png', 
      path: '/bagger/mini-bagger-qv15',
      badge: 'Neu'
    },
  ];

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* Premium Header - Minimal & Clean */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">Jannesmann</h1>
              <p className="text-xs text-gray-500 -mt-0.5">Baumaschinen</p>
            </div>
            <div className="flex items-center gap-2">
              <button className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <Search size={20} className="text-gray-600" />
              </button>
              <a href="tel:+4920419877499" className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                <Phone size={18} className="text-white" />
              </a>
            </div>
          </div>
        </div>
        
        {/* Status Bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 text-xs">
          <div className="flex items-center gap-1 text-green-700">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
            <span className="font-medium">Sofort lieferbar</span>
          </div>
          <div className="flex items-center gap-1 text-gray-600">
            <Clock size={12} />
            <span>Mo-Fr 8-18 Uhr</span>
          </div>
          <div className="flex items-center gap-1 text-gray-600">
            <MapPin size={12} />
            <span>Bottrop</span>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <section className="px-4 py-4">
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-5 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-40 h-40 bg-red-500 rounded-full blur-3xl"></div>
          </div>
          <div className="relative z-10">
            <p className="text-red-400 text-xs font-semibold uppercase tracking-wider mb-1">Qualität aus Deutschland</p>
            <h2 className="text-2xl font-bold mb-2">Baumaschinen<br/>für Profis</h2>
            <p className="text-gray-400 text-sm mb-4">12 Monate Garantie • Sofort lieferbar</p>
            <Link 
              to="/bagger"
              className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
            >
              Sortiment entdecken
              <ChevronRight size={16} className="ml-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Kategorien - Grid mit echten Produktbildern */}
      <section className="px-4 py-2">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-900">Kategorien</h2>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={cat.path}
              className="bg-gray-50 rounded-xl p-3 flex flex-col active:scale-[0.98] transition-transform border border-gray-100"
            >
              <div className="h-20 flex items-center justify-center mb-2">
                <img 
                  src={cat.image} 
                  alt={cat.name}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">{cat.name}</h3>
                <p className="text-xs text-gray-500">{cat.subtitle}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Beliebte Produkte - Horizontal Scroll */}
      <section className="py-4">
        <div className="flex items-center justify-between px-4 mb-3">
          <h2 className="text-lg font-bold text-gray-900">Beliebte Produkte</h2>
          <Link to="/bagger" className="text-red-600 text-sm font-medium flex items-center">
            Alle
            <ChevronRight size={16} />
          </Link>
        </div>
        
        <div className="flex gap-3 overflow-x-auto px-4 pb-2 scrollbar-hide">
          {featuredProducts.map((product) => (
            <Link
              key={product.id}
              to={product.path}
              className="flex-shrink-0 w-40 bg-white rounded-xl border border-gray-200 overflow-hidden active:scale-[0.98] transition-transform shadow-sm"
            >
              {/* Badge */}
              {product.badge && (
                <div className="px-3 pt-2">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                    product.badge === 'Bestseller' ? 'bg-amber-100 text-amber-700' :
                    product.badge === 'Neu' ? 'bg-blue-100 text-blue-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {product.badge}
                  </span>
                </div>
              )}
              
              {/* Image */}
              <div className={`h-28 flex items-center justify-center p-3 ${!product.badge ? 'pt-4' : ''}`}>
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              
              {/* Info */}
              <div className="p-3 pt-0">
                <h3 className="font-bold text-gray-900">{product.name}</h3>
                <p className="text-xs text-gray-500 mb-2">{product.subtitle}</p>
                <p className="text-red-600 font-bold">{product.price} €</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Service Banner */}
      <section className="px-4 py-2">
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Beratung gewünscht?</h3>
              <p className="text-sm text-gray-500">Wir helfen Ihnen gerne</p>
            </div>
            <a 
              href="tel:+4920419877499"
              className="bg-gray-900 text-white text-sm font-semibold px-4 py-2.5 rounded-lg flex items-center"
            >
              <Phone size={16} className="mr-2" />
              Anrufen
            </a>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="px-4 py-4">
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
            <p className="text-xl font-bold text-gray-900">12</p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Monate Garantie</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
            <p className="text-xl font-bold text-gray-900">24h</p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Versand</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
            <p className="text-xl font-bold text-gray-900">100%</p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Service</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PWAHomeScreen;
